import { useState, useEffect, useCallback } from 'react';

/**
 * Options for the useElapsedTime hook.
 */
export interface UseElapsedTimeOptions {
  /** ISO timestamp when the job started (null if not started) */
  startedAt: string | null;
  /** Elapsed seconds from server (synced on each poll) */
  serverElapsedSeconds: number | null;
  /** Estimated remaining seconds from server (null if not available) */
  estimatedRemainingSeconds: number | null;
  /** Polling interval in ms (default: 2000) - used for sync timing */
  pollingInterval?: number;
}

/**
 * Return value from useElapsedTime hook.
 */
export interface ElapsedTimeResult {
  /** Human-readable elapsed time (e.g., "1m 30s") */
  formattedElapsed: string;
  /** Human-readable remaining time (null if not available) */
  formattedRemaining: string | null;
  /** Raw elapsed seconds */
  elapsedSeconds: number;
  /** Whether the job is currently running */
  isRunning: boolean;
}

/**
 * Hook that provides real-time elapsed time tracking for async jobs.
 *
 * Features:
 * - Syncs with server elapsed time on each poll
 * - Increments locally between polls for smooth UX
 * - Formats duration in human-readable format
 * - Supports estimated remaining time display
 *
 * @example
 * ```tsx
 * const { formattedElapsed, formattedRemaining, isRunning } = useElapsedTime({
 *   startedAt: jobStatus.started_at,
 *   serverElapsedSeconds: jobStatus.elapsed_seconds,
 *   estimatedRemainingSeconds: jobStatus.estimated_remaining_seconds
 * });
 *
 * if (isRunning) {
 *   return <span>{formattedElapsed}</span>;
 * }
 * ```
 */
export function useElapsedTime({
  startedAt,
  serverElapsedSeconds,
  estimatedRemainingSeconds,
}: UseElapsedTimeOptions): ElapsedTimeResult {
  const [localElapsed, setLocalElapsed] = useState(0);

  // Sync with server when we receive an update
  useEffect(() => {
    if (serverElapsedSeconds !== null) {
      setLocalElapsed(serverElapsedSeconds);
    }
  }, [serverElapsedSeconds]);

  // Increment locally every second for smooth UX between polls
  useEffect(() => {
    if (!startedAt) return;

    const interval = setInterval(() => {
      setLocalElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt]);

  /**
   * Format seconds into human-readable duration string.
   * - Under 1 min: "Xs"
   * - Under 1 hour: "Xm Xs"
   * - 1+ hours: "Xh Xm"
   */
  const formatDuration = useCallback((seconds: number): string => {
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    }
  }, []);

  return {
    formattedElapsed: formatDuration(localElapsed),
    formattedRemaining: estimatedRemainingSeconds
      ? formatDuration(estimatedRemainingSeconds)
      : null,
    elapsedSeconds: localElapsed,
    isRunning: startedAt !== null,
  };
}
